/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { lazy, type ReactElement, Suspense, useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useBasketSnapshot } from '@/providers/basket';
import { useMiniCartDataLoader } from '@/hooks/use-mini-cart-data';
import { setMiniCartOpen, useMiniCartStore } from '@/hooks/mini-cart-store';
import CartBadgeIcon from './cart-badge-icon';
import { useTranslation } from 'react-i18next';
import type { Brand } from '@/lib/brand/brand';
import { useBrand } from '@/lib/brand/use-brand';
import { HEADER_COMPOSITION } from '@/lib/brand/header-composition';
import { brandCopyFor } from '@/lib/brand/copy';
import { Link } from '@/components/link';
import { routes } from '@/route-paths';
import { cn } from '@/lib/utils';

const CartSheet = lazy(() => import('./cart-sheet'));
const BgMiniCartDropdown = lazy(() => import('./bg-mini-cart-dropdown'));

/*
 * Per-brand mini-cart surface — component selection by brand key, never inline
 * branches (pattern: NAV_SWITCH, commit f25b349). Both surfaces share the same
 * contract ({children} = the badge trigger button) and the same mini-cart store,
 * so the badge logic below is brand-agnostic; only the mounted surface differs.
 *
 * nm: full-height right-side drawer (CartSheet) — auto-opens after add-to-bag.
 *     Source: .reference/mini-cart-open.desktop.html, whose companion
 *     .reference/mini-cart-open.desktop.styles.json records the live URL it was
 *     taken from as a neimanmarcus.com PDP "(drawer opened after Add to Bag)" —
 *     so the capture witnesses BOTH halves of this arm, the drawer form and the
 *     auto-open trigger. Brand confirmed inside the artifact rather than from
 *     its filename, which carries no brand prefix: neimanmarcus.com occurs 74
 *     times in it against 1 for bergdorfgoodman.com.
 * bg: compact dropdown anchored under the bag icon — click-opened only; auto-open
 *     is suppressed at the source (lib/brand/mini-cart-behavior.ts:25, which
 *     cites docs/archetypes/bg-deltas.md §2 MiniCartDT and carries the same
 *     capture reference at :27). Source: .reference/bg-mini-cart-open.desktop.html,
 *     companion .reference/bg-mini-cart-open.desktop.styles.json recording a
 *     bergdorfgoodman.com PDP URL; bergdorfgoodman.com occurs 802 times in the
 *     dump against 3 for neimanmarcus.com.
 */
const MINI_CART_SURFACE = {
    nm: CartSheet,
    bg: BgMiniCartDropdown,
} as const;

/**
 * The count that sits BESIDE the desktop text label, per brand — a COMPLETE
 * class string so Tailwind's scanner can see it, brand-keyed rather than an
 * inline branch (same idiom as `MINI_CART_SURFACE` directly above).
 *
 * WHY THIS EXISTS, AND WHY EVERY EARLIER PASS MISSED IT. Live BG carries TWO
 * count mechanisms, one per viewport, and its own stylesheet gates them at
 * exactly our `bgnav` boundary:
 *
 *     @media (max-width:1024px){.minicart-dt__desktop{display:none!important}}
 *     @media (min-width:1025px){.minicart-dt__mobile {display:none!important}}
 *
 * (.reference/dumps/bg-plp-d-1280/dt-plp-1280-bg_files/
 * client.d19794a33980fdba6d07.bundle.css, offset 404414 — the hash live's own
 * <link rel="stylesheet"> names in bg-footer-content-restaurants.desktop.html.)
 *
 * BELOW 1025 the count is the span overlaid on the bag art, which
 * cart-badge-icon.tsx already renders. AT 1025+ live has no bag art at all, and
 * the count is a SIBLING SPAN AFTER THE WORD "Bag":
 *
 *     <button class="minicart-dt__container__header__btn">
 *       <span>Bag</span><span class="minicart-dt__container__header__qty">1</span>
 *     </button>
 *
 * We rendered neither at desktop, because cart-badge-icon.tsx gates the count
 * with the GLYPH ("the count overlay is part of the glyph, so it goes with it")
 * — true of the mobile mechanism and false of this one. The owner reported it
 * as the bag icon not reflecting a count.
 *
 * WHY IT SURVIVED SO LONG: `minicart-dt__container__header__qty` occurs ZERO
 * times in bg-homepage / bg-pdp-regular / bg-pdp-sale / bg-plp-womens-dresses /
 * bg-search-dress .desktop.html and ONCE in bg-mini-cart-open.desktop.html and
 * bg-footer-content-restaurants.desktop.html (positive control `<div>` 493-1926
 * in every file, `/usr/bin/grep -ao … | wc -l`, raw dumps so no halving). The
 * zero pages were all captured with an EMPTY bag, and an empty-bag capture is
 * structurally incapable of witnessing a count — live's empty-bag desktop
 * markup is a plain `<a class="minicart-dt__container__header__link"
 * href="/cart/">Bag</a>` with no qty span at all. That empty/non-empty pair is
 * also the evidence for the `numberOfItems > 0` gate at the render below.
 *
 * EVERY DECLARATION BELOW IS LIVE'S OWN, at offset 402738 of that same bundle:
 *
 *     position:absolute      -> absolute
 *     left:100%              -> left-full
 *     top:50%                -> top-1/2
 *     transform:translateY(-50%) -> -translate-y-1/2
 *     margin-left:5px        -> ml-[5px]
 *     width:20px;height:20px -> size-5
 *     border-radius:100%     -> rounded-full
 *     background:#000        -> bg-primary            (tokens/core.css:13 #000000)
 *     color:#fff             -> text-primary-foreground (tokens/core.css:14 #ffffff)
 *     font-size:12px         -> text-[12px]
 *     font-weight:600        -> font-semibold
 *     line-height:20px       -> leading-[20px]
 *     letter-spacing:.2px    -> tracking-[0.2px]
 *     text-align:center      -> text-center
 *
 * Two notes rather than a silent approximation. `rounded-full` is 9999px where
 * live writes 100%; on a 20x20 square both paint the same circle. Live also
 * declares `display:inline-block`, which `position:absolute` blockifies, so it
 * is inert and is not transcribed. The semantic colour pair is used instead of
 * raw utilities because `bg-black`/`text-white` are `custom/color-linter`
 * errors here, and `--primary` / `--primary-foreground` are declared once in
 * the shared core with no brand override — they ARE live's #000/#fff, checked,
 * not assumed.
 *
 * UNMEASURED: live's own anchor is the `__btn`, which has `padding:11.5px 0`
 * — no horizontal padding — so its right edge IS the right edge of the word.
 * Ours anchors on the label span for the same reason: our trigger Button
 * carries `--header-cart-box-pad-x` on the right, and anchoring there would put
 * the circle 16px further out than live's. Font family is inherited on both
 * sides (live: `.minicart-dt{font-family:Larsseit}`), so nothing is declared
 * for it here.
 *
 * NM'S ARM IS THE EMPTY STRING AND IS NEVER READ: the whole block is gated on
 * `desktopUtilities === 'text-links'`, which is BG's value alone. It is a
 * `Record<Brand, string>` anyway so that a third brand is a COMPILE ERROR
 * rather than a silent inheritance of BG's badge — the same reason the maps
 * either side of it are keyed.
 */
const DESKTOP_BAG_COUNT: Record<Brand, string> = {
    nm: '',
    bg: 'absolute left-full top-1/2 -translate-y-1/2 ml-[5px] size-5 rounded-full bg-primary text-primary-foreground text-[12px] font-semibold leading-[20px] tracking-[0.2px] text-center',
};

/**
 * WHAT THE BAG DOES BELOW THE BRAND'S DESKTOP BREAKPOINT.
 *
 * This is a MECHANISM difference, not a styling one, and it is the last
 * `[ours-extra]` left on this control: we used to open a mini-cart panel on
 * BOTH brands at every width. Live BG has no mobile panel at all — below 1025
 * its bag is a plain link to the cart page.
 *
 * ── nm: 'mini-cart' ──────────────────────────────────────────────────────
 * The bag opens the drawer at every width. Unchanged, and NM's rendered DOM is
 * byte-identical to what it was before this map existed: the `cart-link` arm
 * below is the ONLY thing that adds an element, and `mobileBag.kind` is never
 * `'cart-link'` for NM. Source: .reference/mini-cart-open.desktop.html and its
 * companion .reference/mini-cart-open.desktop.styles.json, whose recorded URL
 * is a neimanmarcus.com PDP with the drawer opened after Add to Bag — the same
 * pair cited by MINI_CART_SURFACE above.
 *
 * ── bg: 'cart-link' ──────────────────────────────────────────────────────
 * Live BG ships TWO bag controls in one markup block and gates them with a
 * media query, which is why this is a class gate here rather than a JS width
 * branch — we are transcribing live's own mechanism, not inventing one.
 *
 * From live BG's served production stylesheet
 * (.reference/dumps/bg-plp-d-1280/dt-plp-1280-bg_files/
 * client.d19794a33980fdba6d07.bundle.css), quoted verbatim:
 *
 *     @media (max-width:1024px){.minicart-dt__desktop{display:none!important}}
 *     @media (min-width:1025px){.minicart-dt__mobile {display:none!important}}
 *
 * and the whole of the mobile arm's styling — three selectors in a 9,554-rule
 * bundle, counted with `/usr/bin/grep -ao` on the raw file:
 *
 *     .minicart-dt__mobile        (the display gate above)
 *     .minicart-dt__mobile__link  {display:inline-block;position:relative}
 *     .minicart-dt__mobile__qty   {…20px black circle…}
 *
 * No panel, no drawer, no sheet, no scrim: there is nothing in live's mobile
 * arm but a link and a count badge. The matching markup is in
 * .reference/bg-homepage.mobile.html — a MOBILE capture of live BG with a
 * NON-EMPTY bag (both arms carry a `__qty`, so this is not an empty-state
 * artifact), where the two arms read:
 *
 *     <div class="minicart-dt__desktop">…<button class="…__header__btn">…
 *     <div class="minicart-dt__mobile"><a class="minicart-dt__mobile__link"
 *                                         href="/cart/">…<span class="…__qty">
 *
 * Positive control for those greps: 351 `<div` in that file, 9,554 `{` in the
 * bundle. The bag-state caveat matters and is why the mobile capture was used
 * rather than a desktop one — BG's minicart markup CHANGES once the bag is
 * non-empty, and an empty-bag capture cannot witness either `__qty`.
 *
 * ── THE GATE STRINGS ─────────────────────────────────────────────────────
 * COMPLETE literal class strings, never templated: Tailwind's scanner reads
 * source text, so a gate assembled at runtime from `breakpoint` would put the
 * class on the element and emit no rule for it. `bgnav` is our named screen for
 * live's 1025 (theme/tailwind.css; HEADER_COMPOSITION.bg.breakpoint), so these
 * two strings ARE the two media queries quoted above — `bgnav:hidden` is
 * `@media(min-width:1025px){display:none}` and `max-bgnav:hidden` is
 * `@media(max-width:1024px){display:none}`. They must stay complements: a gap
 * shows both controls, an overlap shows neither.
 */
type MobileBagAffordance =
    | { kind: 'mini-cart' }
    | {
          kind: 'cart-link';
          /** Hides the cart LINK at and above the brand's desktop breakpoint. */
          linkGate: string;
          /** Hides the mini-cart TRIGGER below the brand's desktop breakpoint. */
          triggerGate: string;
      };

const MOBILE_BAG: Record<Brand, MobileBagAffordance> = {
    nm: { kind: 'mini-cart' },
    bg: { kind: 'cart-link', linkGate: 'bgnav:hidden', triggerGate: 'max-bgnav:hidden' },
};

/**
 * The cart badge defers the loading of the mini cart sheet until the very first user interaction
 * with the cart icon. The loading of the sheet component itself could in theory also happen earlier,
 * e.g. right after the initial load on the client. Subject for experiments...
 */
export default function CartBadge(): ReactElement {
    const snapshot = useBasketSnapshot();
    const { t } = useTranslation('cart');
    const numberOfItems = snapshot?.uniqueProductCount ?? 0;
    const [clicked, setClicked] = useState<boolean>(false);
    const miniCartOpen = useMiniCartStore((s) => s.open);
    const brand = useBrand();
    // Brand-keyed surface: NM drawer / BG anchored dropdown (see MINI_CART_SURFACE).
    const MiniCartSurface = MINI_CART_SURFACE[brand];
    // Brand-keyed PRESENTATION — lib/brand/header-composition.ts. BG's desktop
    // bag is the word "Bag", not a glyph (bg-home-d-1280/App.tsx:107). This
    // changes only what the trigger LOOKS like: at BG's breakpoint and above,
    // clicking it still opens the brand's anchored dropdown, an already-accepted
    // brand fact (MINI_CART_BEHAVIOR.bg / bg-deltas.md §2 "MiniCartDT").
    //
    // WHAT CHANGED 2026-08-22, and it retires a claim that used to sit here.
    // This comment read: "the static capture's plain `<a href='/cart/'>` is not
    // evidence enough to retire it." That anchor is not a capture artifact — it
    // is live BG's `.minicart-dt__mobile__link`, one of exactly two bag controls
    // live ships, and live's own stylesheet gates the pair at 1024/1025. So the
    // dropdown survives ABOVE the gate, which is where every capture that
    // witnesses it was taken, and BELOW the gate live navigates. See MOBILE_BAG
    // above for the verbatim media queries and the mobile capture.
    const composition = HEADER_COMPOSITION[brand];
    // Brand-keyed MECHANISM below the breakpoint — see MOBILE_BAG above.
    const mobileBag = MOBILE_BAG[brand];
    /* Brand-keyed ACCESSIBLE NAME — lib/brand/copy.ts, `cartBadge`. Ours used
       to announce "My cart, number of items: N", a phrase neither brand says:
       live NM's bag is named "Shopping bag" with no count, live BG's is
       "Shopping Bag containing N items". Computed once and used by all three
       button branches below (fallback / loaded / unclicked) so they cannot
       drift apart. `count` is passed unconditionally — NM's string simply has
       no placeholder to consume it. */
    const cartBadgeName = brandCopyFor(brand).cartBadge;
    const cartBadgeAriaLabel = t(cartBadgeName.key, {
        count: numberOfItems,
        defaultValue: cartBadgeName.defaultValue,
    });
    // The GLYPH arm — the bag art plus the count overlaid on it. On a
    // `text-links` brand CartBadgeIcon gates its own subtree to below the
    // breakpoint, so this arm is mobile-only for BG by construction; on NM it
    // is the whole control at every width.
    const glyphArm = <CartBadgeIcon numberOfItems={numberOfItems} />;
    // The TEXT arm — the word "Bag" and the count beside it, both gated to the
    // breakpoint and up. `relative` makes this span the positioning context for
    // the desktop count, mirroring live's own `__btn`, which is
    // `position:relative` and carries no horizontal padding — see
    // DESKTOP_BAG_COUNT above for why the anchor cannot be the trigger Button.
    // The gate string itself is untouched, so the paired mobile/desktop
    // breakpoint claim the tests pin is unchanged. NM's arm is `null`, which
    // React renders as nothing exactly as the previous `&&`-false did.
    const textArm =
        composition.desktopUtilities === 'text-links' ? (
            <span className={`relative ${composition.desktopInline}`}>
                {t('badge.label', { defaultValue: 'Bag' })}
                {numberOfItems > 0 && (
                    <span className={DESKTOP_BAG_COUNT[brand]} data-testid="bag-count-desktop">
                        {numberOfItems}
                    </span>
                )}
            </span>
        ) : null;
    // WHICH ARM GOES IN THE MINI-CART TRIGGER. On a `cart-link` brand the glyph
    // moves OUT of the trigger and into the cart link below, because the trigger
    // itself no longer exists below the breakpoint and the glyph does not exist
    // above it — keeping both here would emit `shopping-cart-icon` twice, once
    // in a subtree that can never be visible at any width. On a `mini-cart`
    // brand the trigger keeps both arms and its DOM is unchanged.
    const badgeContent =
        mobileBag.kind === 'cart-link' ? (
            textArm
        ) : (
            <>
                {glyphArm}
                {textArm}
            </>
        );

    // Warm the mini-cart data path on hover/focus/touch so the basket and product details (images, variations,
    // promotions) are ideally available by the time the user clicks. The loader handles dedup; the cart sheet's open
    // state is unchanged — only data fetching starts sooner.
    //
    // Skip when no basketId is known (no snapshot, no prior basket call) — without an existing basket there's nothing
    // to enrich, and triggering the resource route would force a network call on a low-engagement hover. Empty baskets
    // (basketId set, items=0) are also skipped.
    const loadMiniCartData = useMiniCartDataLoader();
    const hasBasketId = Boolean(snapshot?.basketId);
    const hasItems = numberOfItems > 0;
    const prefetch = useCallback(() => {
        if (!hasBasketId || !hasItems) {
            return;
        }
        loadMiniCartData();
    }, [hasBasketId, hasItems, loadMiniCartData]);

    // Ensure CartSheet is lazy-loaded when opened externally (e.g. add-to-cart)
    if (miniCartOpen && !clicked) {
        setClicked(true);
    }

    // The trigger's gate — empty for a `mini-cart` brand, so `cn()` drops it and
    // NM's three class strings stay byte-identical to what the storybook
    // snapshots pin. See MOBILE_BAG for why this is a class and not a JS width
    // read.
    const triggerGate = mobileBag.kind === 'cart-link' ? mobileBag.triggerGate : '';

    // THE MOBILE ARM — live BG's `a.minicart-dt__mobile__link[href="/cart/"]`.
    // It is a real anchor, so it navigates without JS, announces as a link, and
    // supports open-in-new-tab — none of which a button that calls `navigate()`
    // would. It carries the same box classes as the trigger it replaces so the
    // header row's geometry is unchanged; only the display gate differs.
    //
    // NO PREFETCH HERE ON PURPOSE: `loadMiniCartData` warms the mini-cart's
    // consolidated basket+products call, and this control opens no mini-cart.
    // Below the gate there is nothing to warm.
    //
    // `false` on a `mini-cart` brand, so NM renders nothing here and the
    // fragment below holds exactly one child — the same element it returned
    // before.
    const mobileCartLink = mobileBag.kind === 'cart-link' && (
        <Button
            asChild
            variant="ghost"
            className={cn(
                `relative cursor-pointer md:h-(--header-cart-box-height) md:w-(--header-cart-box-width) ${composition.cartBoxPadX} md:py-(--header-cart-box-pad-y) hover:bg-transparent hover:opacity-50 transition-opacity`,
                composition.cartTriggerMobilePadX,
                mobileBag.linkGate
            )}>
            <Link to={routes.cart} aria-label={cartBadgeAriaLabel} data-testid="header-bag-cart-link">
                {glyphArm}
            </Link>
        </Button>
    );

    // Icons match live rects; framework Button padding stripped — divergences
    // from live are flagged to the owner, never rationalized (see handoff rule 8c).
    // Live "Cart container": 20x26, zero padding, flush right at the 30px frame
    // line (the last icon in the row; the column right-aligns it). The box takes
    // the --header-cart-box-* tokens at md+; mobile keeps the stock tap target.
    if (clicked) {
        return (
            <>
                {mobileCartLink}
                <Suspense
                    fallback={
                        <Button
                            variant="ghost"
                            className={cn(
                                `relative pointer-events-none md:h-(--header-cart-box-height) md:w-(--header-cart-box-width) ${composition.cartBoxPadX} md:py-(--header-cart-box-pad-y) hover:bg-transparent`,
                                composition.cartTriggerMobilePadX,
                                triggerGate
                            )}
                            aria-label={cartBadgeAriaLabel}>
                            {badgeContent}
                        </Button>
                    }>
                    <MiniCartSurface>
                        <Button
                            variant="ghost"
                            className={cn(
                                `relative cursor-pointer md:h-(--header-cart-box-height) md:w-(--header-cart-box-width) ${composition.cartBoxPadX} md:py-(--header-cart-box-pad-y) hover:bg-transparent hover:opacity-50 transition-opacity`,
                                composition.cartTriggerMobilePadX,
                                triggerGate
                            )}
                            onPointerEnter={prefetch}
                            onFocus={prefetch}
                            aria-label={cartBadgeAriaLabel}>
                            {badgeContent}
                        </Button>
                    </MiniCartSurface>
                </Suspense>
            </>
        );
    }

    return (
        <>
            {mobileCartLink}
            <Button
                variant="ghost"
                className={cn(
                    `relative cursor-pointer md:h-(--header-cart-box-height) md:w-(--header-cart-box-width) ${composition.cartBoxPadX} md:py-(--header-cart-box-pad-y) hover:bg-transparent hover:opacity-50 transition-opacity`,
                    composition.cartTriggerMobilePadX,
                    triggerGate
                )}
                onClick={() => {
                    setClicked(true);
                    setMiniCartOpen(true);
                }}
                onPointerEnter={prefetch}
                onFocus={prefetch}
                aria-label={cartBadgeAriaLabel}>
                {badgeContent}
            </Button>
        </>
    );
}
